<template>
  <div class="booking-overview">
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingSelectList"
        />
      </div>
    </div>
    <b-card title="預約總覽">
      <b-overlay :show="showLoading" rounded="sm">
        <b-row no-gutters class="mt-2 mb-3">
          <b-col sm="4" lg="3">
            <b-form-select v-model="query.preset_id" :options="bookingPresetsOptions" style="height: 100%" />
          </b-col>
          <b-col sm="4" lg="3" class="ml-2">
            <AppDatePicker
              v-model="selectedDate"
              placeholder="選擇日期"
              @input="handleDateChanged"
            />
          </b-col>
          <b-button
            v-b-toggle.collapse-1-inner
            variant="link"
            class="flex-shrink-0 ml-2"
          >
            進階搜尋<i class="fa fa-chevron-down ml-2"></i>
          </b-button>
          <h6 class="text-center mt-2 ml-auto">預約筆數：{{ fetchCount }} / 總筆數：{{ totalCount }}</h6>
        </b-row>
        <b-collapse id="collapse-1-inner">
          <div class="mt-2 mb-3 d-flex flex-column">
            <AppMultiSelect
              class="mb-2"
              style="order: 0"
              v-model="query.status"
              :options="statusOptions"
              :disabled="showLoading || !this.query.preset_id"
            />
            <AppSingleSelect
              v-if="showBranchSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.branch', 1) }"
              v-model="branch"
              :options="branchOptions"
              @input="handleBranchFilterChanged"
              :disabled="showLoading || !this.checkPermission([consts.BOOKING_LIST_BRANCH])"
            />
            <b-select
              v-if="showStaffSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.staff', 2) }"
              :options="staffOptions"
              v-model="query.staff_id"
              @input="handleStaffFilterChanged"
              :disabled="showLoading || staffOptions.length <= 1"
            ></b-select>
            <b-select
              v-if="showServiceSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.service', 3) }"
              :options="serviceOptions"
              v-model="query.service_id"
              @input="handleServiceFilterChanged"
              :disabled="showLoading"
            ></b-select>
          </div>
        </b-collapse>
        <BookingSeats
          v-if="bookingPresetModel.maximumBookingSlotCount > 0"
          :time-periods="timePeriods"
          :staffs="staffs"
          :services="services"
          :bookings="bookingList"
          :maximum-booking-slot-count="bookingPresetModel.maximumBookingSlotCount"
          :preset-id="query.preset_id"
          :show-mode-selection="false"
          :enable-booking-creation="false"
          :cell-min-height="60"
        />
        <div v-else class="text-center p-4">
          <p class="text-muted">此預約設定檔未啟用「預約次數限制」</p>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import BookingSeats from '@/components/Dashboard/Booking/BookingSeats.vue'
import bookingApi from '@/apis/booking'
import bookingPresetApi from '@/apis/booking-preset'
import collectionApi from "@/apis/collection"
import BookingPresetDataModel from "@/models/BookingPreset.model"
import DynamicFormField from "@/models/DynamicFormField.model.js";
import BookingPresenter from "@/presenters/Booking.presenter.js"
import BookingBusinessLogic from "@/utils/booking/BookingBusinessLogic"
import ServiceDataFetcher from "@/utils/booking/ServiceDataFetcher"
import StaffDataFetcher from "@/utils/booking/StaffDataFetcher"
import _ from "lodash"
import moment from "moment"
import Vue from "vue"
import { mapGetters } from "vuex";
import * as consts from "@/consts";
import PermissionChecker from '@/utils/PermissionChecker';
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";
import AppMultiSelect from "@/components/AppMultiSelect";
import AppDatePicker from "@/components/AppDatePicker.vue";

const servicesForFilterDataFetcher = Vue.observable(new ServiceDataFetcher())
servicesForFilterDataFetcher.injectCollectionApi(collectionApi)

const staffsForFilterDataFetcher = Vue.observable(new StaffDataFetcher())
staffsForFilterDataFetcher.injectBookingPresetApi(bookingPresetApi)
const staffsForBranchDataFetcher = Vue.observable(new StaffDataFetcher())
staffsForBranchDataFetcher.injectBookingPresetApi(bookingPresetApi)

const STORAGE_KEY_LAST_PRESET_ID = 'booking_overview_last_preset_id';
const STORAGE_KEY_STATUS_FILTER = 'booking_overview_status_filter';

export default {
  components: {
    BookingSeats,
    AppSingleSelect,
    BreadCrumb,
    AppMultiSelect,
    AppDatePicker
  },
  data() {
    const today = moment()

    return {
      consts: consts,
      isFetchingPresets: false,
      isFetchingBookings: false,
      isFetchingCollectionsOfCurrentBookingPresetFields: false,
      bookingPresets: [],
      collectionsOfCurrentBookingPresetFields: [],
      bookings: [],
      bookingStatusOptionResponse: null,
      selectedDate: today.format('YYYY-MM-DD'),
      query: {
        preset_id: localStorage.getItem(STORAGE_KEY_LAST_PRESET_ID) || null,
        service_id: null,
        branch_id: null,
        staff_id: null,
        status: localStorage.getItem(STORAGE_KEY_STATUS_FILTER) ? JSON.parse(localStorage.getItem(STORAGE_KEY_STATUS_FILTER)) : [],
        start_at: today.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end_at: today.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      },
      fetchCount: 0,
      totalCount: 0,
      branch: {},
    }
  },
  computed: {
    ...mapGetters('general', [
      'myBranches',
      'currentBranch'
    ]),
    queryWatcher() {
      const statusStr = Array.isArray(this.query.status) ? this.query.status.join(',') : this.query.status
      return `${this.query.preset_id}-${statusStr}-${this.query.service_id}-${this.query.branch_id}-${this.query.staff_id}-${this.query.start_at}-${this.query.end_at}`;
    },
    serviceIdQueryInArray() {
      if (!this.query.service_id) {
        return []
      }
      return Array.isArray(this.query.service_id) ? this.query.service_id : [this.query.service_id]
    },
    currentBookingPreset() {
      return this.bookingPresets.find(booking => booking.id === this.query.preset_id)
    },
    bookingPresetModel() {
      return new BookingPresetDataModel(this.currentBookingPreset)
    },
    bookingBusinessLogic() {
      return new BookingBusinessLogic(this.bookingPresetModel)
    },
    bookingPresetsOptions() {
      return [
        { text: '請選擇', value: null },
        ...this.bookingPresets.map(preset => ({ text: preset.name, value: preset.id }))
      ]
    },
    showBranchSelect() {
      return this.bookingPresetModel.branchFieldId
    },
    showStaffSelect() {
      return this.bookingPresetModel.isWizardMode
    },
    showServiceSelect() {
      return this.bookingPresetModel.isWizardMode && this.bookingPresetModel.collectionIdForService
    },
    serviceOptions() {
      return [
        { value: null, text: "選擇服務" },
        ...servicesForFilterDataFetcher.services.map(service => ({ value: service.id, text: service.title }))
      ]
    },
    staffOptions() {
      return [
        { value: null, text: '請選擇員工' },
        ...staffsForFilterDataFetcher.staffs.map(staff => ({ value: staff.id, text: staff.name }))
      ]
    },
    branchOptions() {
      return [
        { value: null, text: '請選擇分店' },
        ...this.myBranches.map(branch => ({ value: branch.id, text: `${branch.branch_code} ${branch.name}` }))
      ]
    },
    statusOptions() {
      return [
        ..._.get(this.bookingStatusOptionResponse, 'data.status_mapping', [])
          .map(status => ({ value: status, text: status }))
      ]
    },
    showLoading() {
      return this.isFetchingPresets || this.isFetchingBookings || this.isFetchingCollectionsOfCurrentBookingPresetFields
    },
    timePeriods() {
      const timeOptions = this.bookingBusinessLogic.generateTimeOptionsForDate(moment(this.selectedDate).format('YYYY-MM-DD'))
      const gap = this.bookingPresetModel.timeOptionGap

      return timeOptions.map((option, index) => ({
        id: index + 1,
        start: option.format('HH:mm'),
        end: gap > 0 ? option.add(gap, 'minutes').format('HH:mm') : null
      }))
    },
    staffs() {
      return staffsForFilterDataFetcher.staffs.map(staff => ({ id: staff.id, name: staff.name }))
    },
    services() {
      return servicesForFilterDataFetcher.services.map(service => ({ id: service.id, name: service.name }))
    },
    bookingList() {
      return this.bookings.map(booking => {
        const bookingPresenter = BookingPresenter.create(
          booking,
          this.currentBookingPreset,
          this.collectionsOfCurrentBookingPresetFields
        )

        return {
          id: booking.id,
          title: bookingPresenter.title,
          startAt: booking.start_at,
          endAt: booking.end_at,
          branchName: bookingPresenter.branchName,
          staffId: booking.staff_id,
          staffName: bookingPresenter.staffName,
          serviceId: booking.service_id,
          serviceName: bookingPresenter.serviceName,
        }
      })
    },
  },
  async mounted() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    await this.fetchBookingPresets()
    this.query.preset_id = this.query.preset_id ?? this.bookingPresets[0]?.id
    if (this.showBranchSelect) {
      this.setBranchFilterToCurrentBranch()
    } else {
      this.$set(this.query, 'branch_id', null)
      this.branch = {}
      this.fetchBookings();
    }
  },
  watch: {
    queryWatcher: {
      handler() {
        localStorage.setItem(STORAGE_KEY_LAST_PRESET_ID, this.query.preset_id);
        localStorage.setItem(STORAGE_KEY_STATUS_FILTER, JSON.stringify(this.query.status))
        updateUrlWithoutReload(this.query, this.$store, this.$route)
        if (this.query.preset_id) {
          this.fetchBookings();
        } else {
          this.bookings = []
          this.bookingStatusOptionResponse = null
        }
      },
    },
    currentBookingPreset: {
      async handler(bookingPreset) {
        if (!bookingPreset) {
          return
        }
        servicesForFilterDataFetcher.setServiceFieldMappings(this.bookingPresetModel.serviceFieldMappings)
        if (this.showBranchSelect) {
          this.setBranchFilterToCurrentBranch()
        }
        this.getStatusOptions()
        await this.fetchCollectionsOfCurrentBookingPresetFields()

        this.query.status = [];
      },
      immediate: true
    },
  },
  methods: {
    deepGet: _.get,
    async handleBranchFilterChanged() {
      this.query.branch_id = this.branch.value

      if (this.bookingBusinessLogic.servicesAreBoundToBranches()) {
        servicesForFilterDataFetcher.fetchServicesByBranch(this.bookingPresetModel.collectionIdForService, this.query.branch_id)
        if (this.branch.value === null) {
          this.query.service_id = null
          this.handleServiceFilterChanged()
        }
      }

      if (this.bookingBusinessLogic.staffsAreBoundToBranches()) {
        staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        if (this.branch.value === null) {
          this.query.staff_id = null
          this.handleStaffFilterChanged()
        }
      }

      staffsForBranchDataFetcher.fetchStaffsByBranch(this.query.branch_id, this.query.preset_id)
    },
    async handleStaffFilterChanged() {
      if (this.bookingBusinessLogic.servicesAreBoundToStaffs()) {
        servicesForFilterDataFetcher.fetchServicesByStaff(this.bookingPresetModel.collectionIdForService, this.query.staff_id)
        if (this.query.staff_id === null) {
          this.query.service_id = null
          this.handleServiceFilterChanged()
        }
      }
    },
    async handleServiceFilterChanged() {
      if (this.bookingBusinessLogic.staffsAreBoundToServices()) {
        staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        if (this.query.service_id === null) {
          this.query.staff_id = null
          this.handleStaffFilterChanged()
        }
      }
    },
    handleDateChanged(date) {
      if (date) {
        // AppDatePicker emits YYYY-MM-DD format
        this.query.start_at = `${date} 00:00:00`
        this.query.end_at = `${moment(date).add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')}`
      }
    },
    async getStatusOptions() {
      this.bookingStatusOptionResponse = await bookingApi.getBookingOptions(this.query.preset_id)
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker;
      return checker.check(permissions);
    },
    async fetchBookingPresets() {
      try {
        this.isFetchingPresets = true
        const response = await bookingApi.getBookingPresets({
          page: 1,
          per_page: 20,
          status: "published"
        });
        this.bookingPresets = response.data.data
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取預約設定檔發生錯誤", "error")
      } finally {
        this.isFetchingPresets = false
      }
    },
    async fetchBookings() {
      try {
        this.isFetchingBookings = true
        const response = await bookingApi.getBookings(this.query.preset_id, {
          start_at: this.query.start_at,
          end_at: this.query.end_at,
          page: 1,
          per_page: 100,
          keyword: '',
          branch_id: this.query.branch_id,
          staff_id: this.query.staff_id,
          service_id: this.query.service_id,
          status: this.query.status.map(s => typeof s === 'string' ? s : s.value),
          with_customer_profile: true,
        })
        this.fetchCount = response.data.meta.to ?? 0
        this.totalCount = response.data.meta.total ?? 0
        this.bookings = _.get(response, 'data.data', [])
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取預約資料發生錯誤", "error")
      } finally {
        this.isFetchingBookings = false
      }
    },
    async fetchCollectionsOfCurrentBookingPresetFields() {
      this.isFetchingCollectionsOfCurrentBookingPresetFields = true
      try {
        this.collectionsOfCurrentBookingPresetFields = await Promise.all(
          DynamicFormField.fromArray(_.get(this.currentBookingPreset, 'config.fields', []))
            .filter(fieldModel => fieldModel.isCollectionField() && fieldModel.collection_id)
            .map(async fieldModel => _.get(await collectionApi.getCollection(fieldModel.collection_id, { with_all_items: true }), 'data.data'))
        )
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取資料集發生錯誤", "error")
      } finally {
        this.isFetchingCollectionsOfCurrentBookingPresetFields = false
      }
    },
    setBranchFilterToCurrentBranch() {
      if (this.currentBranch) {
        this.query.branch_id = this.currentBranch.id
        this.branch = this.branchOptions.find(branch => branch.value === this.currentBranch.id)

        if (this.bookingBusinessLogic.servicesAreBoundToBranches()) {
          servicesForFilterDataFetcher.fetchServicesByBranch(this.bookingPresetModel.collectionIdForService, this.query.branch_id)
        }

        if (this.bookingBusinessLogic.staffsAreBoundToBranches()) {
          staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        }

        staffsForBranchDataFetcher.fetchStaffsByBranch(this.query.branch_id, this.query.preset_id)
      }
    },
  }
}
</script>

<style scoped lang="scss">
</style>
